import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';

export interface MensagemSelecao {
  id: string;
  tipo: string;
  texto: string;
  timestamp: string;
  remetenteTipo: 'cliente' | 'atendente' | 'sistema';
  remetenteNome: string | null;
  ticketId: string;
  protocolo: string | null;
  ticketAtual: boolean;
}

interface Props {
  mensagens: MensagemSelecao[];
  selecionadas: Set<string>;
  onChange: (selecionadas: Set<string>) => void;
  loading?: boolean;
}

const LIMITE = 2000;

// Lista as mensagens anteriores da conversa para quem transfere escolher
// quais o próximo atendente poderá ver. Mensagens do ticket atual primeiro;
// as de protocolos anteriores ficam recolhidas.
const SelecaoMensagensTransferencia: React.FC<Props> = ({ mensagens, selecionadas, onChange, loading }) => {
  const { t } = useTranslation();

  // Grupos por protocolo, em ordem cronológica dentro de cada grupo
  const grupos = useMemo(() => {
    const porTicket = new Map<string, { protocolo: string | null; atual: boolean; itens: MensagemSelecao[] }>();
    for (const m of mensagens) {
      if (!porTicket.has(m.ticketId)) porTicket.set(m.ticketId, { protocolo: m.protocolo, atual: m.ticketAtual, itens: [] });
      porTicket.get(m.ticketId)!.itens.push(m);
    }
    const lista = Array.from(porTicket.entries()).map(([ticketId, g]) => ({
      ticketId,
      ...g,
      itens: [...g.itens].sort((a, b) => +new Date(a.timestamp) - +new Date(b.timestamp)),
    }));
    // Ticket atual primeiro, depois do mais recente para o mais antigo
    return lista.sort((a, b) => {
      if (a.atual !== b.atual) return a.atual ? -1 : 1;
      const ua = a.itens[a.itens.length - 1]?.timestamp || '';
      const ub = b.itens[b.itens.length - 1]?.timestamp || '';
      return ub.localeCompare(ua);
    });
  }, [mensagens]);

  const alternar = (id: string) => {
    const nova = new Set(selecionadas);
    if (nova.has(id)) nova.delete(id); else nova.add(id);
    onChange(nova);
  };

  // Seleciona a mensagem e todas as mais recentes que ela
  const aPartirDaqui = (m: MensagemSelecao) => {
    const limite = +new Date(m.timestamp);
    const nova = new Set(selecionadas);
    mensagens.forEach((x) => { if (+new Date(x.timestamp) >= limite) nova.add(x.id); });
    onChange(nova);
  };

  const selecionarGrupo = (itens: MensagemSelecao[], marcar: boolean) => {
    const nova = new Set(selecionadas);
    itens.forEach((m) => (marcar ? nova.add(m.id) : nova.delete(m.id)));
    onChange(nova);
  };

  const descricao = (m: MensagemSelecao) => {
    if (m.texto) return m.texto;
    const chave = `atendimento.transferMsgType.${m.tipo}`;
    const traduzido = t(chave);
    return `[${traduzido === chave ? m.tipo : traduzido}]`;
  };

  const remetente = (m: MensagemSelecao) => {
    if (m.remetenteTipo === 'cliente') return m.remetenteNome || t('atendimento.transferSenderClient');
    if (m.remetenteTipo === 'sistema') return t('atendimento.transferSenderSystem');
    return m.remetenteNome || t('atendimento.transferSenderAgent');
  };

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {t('atendimento.transferSelectedCount', { selected: selecionadas.size, total: mensagens.length })}
        </span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
            disabled={loading || mensagens.length === 0}
            onClick={() => onChange(new Set(mensagens.map((m) => m.id)))}>
            {t('atendimento.transferSelectAll')}
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
            disabled={loading || selecionadas.size === 0}
            onClick={() => onChange(new Set())}>
            {t('atendimento.transferClearSelection')}
          </Button>
        </div>
      </div>

      <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700">
        {loading ? (
          <p className="p-3 text-sm text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
        ) : mensagens.length === 0 ? (
          <p className="p-3 text-sm text-gray-500 dark:text-gray-400">{t('atendimento.transferNoPreviousMessages')}</p>
        ) : (
          grupos.map((g) => {
            const marcadas = g.itens.filter((m) => selecionadas.has(m.id)).length;
            return (
              <details key={g.ticketId} open={g.atual} className="border-b last:border-b-0 border-gray-200 dark:border-gray-700">
                <summary className="flex cursor-pointer items-center justify-between gap-2 bg-gray-50 px-3 py-2 text-xs font-medium dark:bg-slate-700/60">
                  <span>
                    {g.atual ? t('atendimento.transferCurrentTicket') : t('atendimento.transferPreviousTicket')}
                    {g.protocolo ? ` · ${g.protocolo}` : ''} ({marcadas}/{g.itens.length})
                  </span>
                  <button
                    type="button"
                    className="text-primary hover:underline"
                    onClick={(e) => { e.preventDefault(); selecionarGrupo(g.itens, marcadas < g.itens.length); }}
                  >
                    {marcadas < g.itens.length ? t('atendimento.transferSelectAll') : t('atendimento.transferClearSelection')}
                  </button>
                </summary>
                <ul>
                  {g.itens.map((m) => (
                    <li key={m.id} className="group flex items-start gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-700/40">
                      <Checkbox
                        className="mt-0.5"
                        checked={selecionadas.has(m.id)}
                        onCheckedChange={() => alternar(m.id)}
                        aria-label={descricao(m)}
                      />
                      <label className="min-w-0 flex-1 cursor-pointer text-sm" onClick={() => alternar(m.id)}>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {format(new Date(m.timestamp), 'dd/MM HH:mm')} · {remetente(m)}
                        </span>
                        <span className="block truncate">{descricao(m)}</span>
                      </label>
                      <button
                        type="button"
                        className="shrink-0 text-xs text-primary opacity-0 hover:underline focus:opacity-100 group-hover:opacity-100"
                        onClick={() => aPartirDaqui(m)}
                        title={t('atendimento.transferFromHereHint')}
                      >
                        {t('atendimento.transferFromHere')}
                      </button>
                    </li>
                  ))}
                </ul>
              </details>
            );
          })
        )}
      </div>
      {mensagens.length >= LIMITE && (
        <p className="text-xs text-amber-600 dark:text-amber-400">{t('atendimento.transferLimitNotice', { limit: LIMITE })}</p>
      )}
    </div>
  );
};

export default SelecaoMensagensTransferencia;
